import { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const ATHENA_URL = 'https://www.athena4partners.org';

type AthenaDisclaimerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function AthenaDisclaimerDialog({ open, onOpenChange }: AthenaDisclaimerDialogProps) {
  const handleYes = () => {
    window.open(ATHENA_URL, '_blank', 'noopener,noreferrer');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Leaving the Converge @ NPS app</DialogTitle>
          <DialogDescription className="pt-2 text-sm leading-relaxed">
            For your security, please note that you are leaving the Converge @ NPS
            app to open another browser window for Athena.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-foreground font-medium">
          Would you like to proceed?
        </p>
        <DialogFooter className="flex-row gap-2 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            No
          </Button>
          <Button onClick={handleYes}>
            Yes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const BULLETS = [
  'Connect across institutional boundaries & discover relevant needs to deliver critical innovations',
  'Leverage, integrate and collaborate with NPS resources to accelerate your ability to innovate',
  'Enable researchers and partners to understand requirements, priorities and opportunities',
];

export function AthenaDesktopBanner() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen(true);
            }
          }}
          className="w-full rounded-2xl overflow-hidden cursor-pointer relative bg-gradient-to-r from-[hsl(216,100%,15%)] via-[hsl(216,80%,22%)] to-[hsl(195,90%,40%)] p-5 shadow-lg hover:shadow-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-1/3 w-24 h-24 bg-white/5 rounded-full translate-y-1/2" />

          <div className="relative flex items-center gap-6">
            {/* Logo + title */}
            <div className="flex items-center gap-3 shrink-0">
              <img
                src="/athena-icon.webp"
                alt="Athena"
                className="h-14 w-14 rounded-full bg-white p-1.5"
              />
              <div>
                <h3 className="text-2xl font-bold text-white tracking-tight">Athena</h3>
                <p className="text-[11px] text-white/60 italic mt-0.5">A trusted platform for defense<br />collaborative research</p>
              </div>
            </div>

            {/* Bullets — centered in available space, all same width as longest */}
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-stretch gap-1.5">
                {BULLETS.map((bullet) => (
                  <div
                    key={bullet}
                    className="rounded-full border border-amber-400/60 bg-white/10 px-4 py-1.5 text-[10px] text-white/90 text-center whitespace-nowrap"
                  >
                    {bullet}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA button */}
            <Button
              className="shrink-0 bg-[hsl(216,100%,22%)] text-white font-semibold hover:bg-[hsl(216,100%,28%)] border border-white/30 gap-1.5 h-11 px-6"
              tabIndex={-1}
            >
              Go to Athena
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </motion.div>
      <AthenaDisclaimerDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function AthenaMobileCard({ index = 0 }: { index?: number }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Card
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="h-full min-h-[120px] md:min-h-[160px] p-3 md:p-6 flex flex-col hover:shadow-lg transition-all duration-300 border-border/50 hover:border-accent/50 cursor-pointer group hover:scale-[1.02] hover:-translate-y-1 active:scale-[0.98] active:translate-y-0 animate-fade-in"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl overflow-hidden mb-3 md:mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shrink-0">
          <img
            src="/athena-icon.webp"
            alt="Athena"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 flex flex-col">
          <h3 className="font-semibold text-sm md:text-lg mb-1 flex items-center gap-1 md:gap-2 transition-colors duration-300 group-hover:text-accent leading-tight">
            Athena
            <ExternalLink className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground shrink-0" />
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground transition-colors duration-300 line-clamp-1 md:line-clamp-none">Collaborative Research App</p>
        </div>
      </Card>
      <AthenaDisclaimerDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
